import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

/**
 * Port of garage-project-website's `use-fit-text` behaviour.
 * Returns fontSize as a CSS **percent** of the parent (text-9xl / 8rem).
 */
export default function useFitText({
  maxFontSize = 100,
  minFontSize = 20,
  resolution = 5,
  onFinish,
} = {}) {
  const initState = useCallback(() => ({
    calcKey: 0,
    fontSize: maxFontSize,
    fontSizePrev: minFontSize,
    fontSizeMax: maxFontSize,
    fontSizeMin: minFontSize,
  }), [maxFontSize, minFontSize]);

  const ref = useRef(null);
  const isCalculatingRef = useRef(false);
  const onFinishRef = useRef(onFinish);
  onFinishRef.current = onFinish;

  const [state, setState] = useState(initState);
  const {
    calcKey,
    fontSize,
    fontSizeMax,
    fontSizeMin,
    fontSizePrev,
  } = state;

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;

    let frameId = null;
    const observer = new ResizeObserver(() => {
      frameId = window.requestAnimationFrame(() => {
        if (isCalculatingRef.current) return;
        isCalculatingRef.current = true;
        setState((current) => ({
          ...initState(),
          calcKey: current.calcKey + 1,
        }));
      });
    });

    observer.observe(el);

    const start = () => {
      isCalculatingRef.current = true;
      setState((current) => ({
        ...initState(),
        calcKey: current.calcKey + 1,
      }));
    };

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(start);
    } else {
      start();
    }

    return () => {
      if (frameId) window.cancelAnimationFrame(frameId);
      observer.disconnect();
    };
  }, [initState]);

  const innerHtml = ref.current ? ref.current.innerHTML : '';
  const innerHtmlPrevRef = useRef();
  useEffect(() => {
    if (calcKey === 0 || isCalculatingRef.current) return;
    if (innerHtml !== innerHtmlPrevRef.current) {
      isCalculatingRef.current = true;
      setState({
        ...initState(),
        calcKey: calcKey + 1,
      });
    }
    innerHtmlPrevRef.current = innerHtml;
  }, [calcKey, initState, innerHtml]);

  useLayoutEffect(() => {
    if (calcKey === 0 || !ref.current) return;

    const isWithinResolution = Math.abs(fontSize - fontSizePrev) <= resolution;
    const isOverflow = (
      ref.current.scrollHeight > ref.current.offsetHeight
      || ref.current.scrollWidth > ref.current.offsetWidth
    );
    const isFailed = isOverflow && fontSize === fontSizePrev;
    const isAsc = fontSize > fontSizePrev;

    if (isWithinResolution) {
      if (isFailed) {
        isCalculatingRef.current = false;
      } else if (isOverflow) {
        setState({
          calcKey,
          fontSize: isAsc ? fontSizePrev : fontSizeMin,
          fontSizeMax,
          fontSizeMin,
          fontSizePrev,
        });
      } else {
        isCalculatingRef.current = false;
        if (onFinishRef.current) onFinishRef.current(fontSize);
      }
      return;
    }

    let delta;
    let newMax = fontSizeMax;
    let newMin = fontSizeMin;
    if (isOverflow) {
      delta = isAsc ? fontSizePrev - fontSize : fontSizeMin - fontSize;
      newMax = Math.min(fontSizeMax, fontSize);
    } else {
      delta = isAsc ? fontSizeMax - fontSize : fontSizePrev - fontSize;
      newMin = Math.max(fontSizeMin, fontSize);
    }

    setState({
      calcKey,
      fontSize: fontSize + delta / 2,
      fontSizeMax: newMax,
      fontSizeMin: newMin,
      fontSizePrev: fontSize,
    });
  }, [
    calcKey,
    fontSize,
    fontSizeMax,
    fontSizeMin,
    fontSizePrev,
    resolution,
  ]);

  return {
    fontSize: `${fontSize}%`,
    ref,
  };
}
